import { useFormContext } from 'react-hook-form'
import { AxiosResponse } from 'axios'
import { HdsUserUpdateInfoRequestResBody } from '@saas/interfaces/core'
import { makePointer } from '../../../api/_utils'
import { capitalizeFirstLetter } from '../../../utils/stringUtils'
import {
    BuildFormValuesFromRppsProps,
    SortAlphabeticallyObj,
    SpeMentionRef,
    UserData,
    UserFormConfig,
} from '../_props'
import { civilitiesMap, titles, titlesMap } from '../userFormData'
import { UserInfoEditionRequest } from './_props'
import { getHdsRequester } from '../../../api/hds'

export const processCivility = (civility: string | undefined) => {
    if (!civility) return undefined

    if (civility.toLocaleLowerCase() === 'mlle') return civilitiesMap.mme.value
    return civility
}

export const useFormContextUser = useFormContext<UserData>

export const sortAlphabetically = <T extends SortAlphabeticallyObj>(array: T[]) =>
    array.sort((a, b) => a.label.localeCompare(b.label))

export const UserRoleCodes = {
    SUPERUSER: 0,
    MANAGER: 1,
    SIMPLE: 2,
}

export const buildFormValuesFromRpps = ({
    userRpps,
    annData,
}: BuildFormValuesFromRppsProps): Partial<UserData> => {

    const userTitle = titles.filter(
        (title) => title.value.toLowerCase() === userRpps?.codecivilitedexercice?.toLowerCase(),
    )
    const title = userTitle[0]
    const rppsProfession = annData?.profession?.name ?? ''
    const rppsSpeciality = annData?.speciality
    const rppsQualification = annData?.qualifications?.[0]
    const mssEmails = annData?.mssEmails && annData.mssEmails.length > 0 ? annData?.mssEmails : undefined

    return {
        idnps: annData?.userIdnps ?? '',
        category: annData?.userRole || '',
        categoryList: [{
            label: annData?.userRole ?? '',
            value: annData?.userRole ?? '',
        }],
        securedEmail: mssEmails ? mssEmails[0] : '',
        firstName: capitalizeFirstLetter(userRpps.prenomdexercice),
        lastName: userRpps.nomdexercice.toUpperCase(),
        civility: processCivility(userRpps.codecivilite),
        title: title?.value ?? titlesMap.none.value,
        speMentions: [],
        validRpps: true,
        rpps: userRpps.identifiantpp.toString(),
        profession: rppsProfession || '',
        speciality: rppsSpeciality?.name || '',
        specialityId: rppsSpeciality?.id || '',
        specialityPointer: rppsSpeciality?.id ? makePointer('Speciality', rppsSpeciality.id) : undefined,
        qualification: rppsQualification?.name || '',
        qualificationId: rppsQualification?.id || '',
        qualificationPointer: rppsQualification?.id ? makePointer('Qualification', rppsQualification.id) : undefined,
    }
}

export const userFormConfig: UserFormConfig<UserData> = {
    fieldsWhitelists: {
        rpps: ['civility', 'title', 'lastName', 'firstName', 'qualification', 'profession', 'speciality', 'category'],
    },
}

export const rppsDefaultData: Partial<UserData> = {
    civility: undefined,
    title: undefined,
    lastName: undefined,
    firstName: undefined,
    qualification: undefined,
    profession: undefined,
    speciality: undefined,
    category: undefined,
    rpps: undefined,
    idnps: undefined,
    securedEmail: undefined,
    validRpps: false,
}

const getRoute = (redirectKey: UserInfoEditionRequest) => {
    if (redirectKey === 'phone') {
        return '/user/update-info-request/phone'
    }
    return '/user/update-info-request/email'
}

export const handleRedirect = async (redirectKey: UserInfoEditionRequest) => {
    const route = getRoute(redirectKey)

    // send creds
    const hdsRequester = await getHdsRequester()
    const response = await hdsRequester.get<never, AxiosResponse<HdsUserUpdateInfoRequestResBody>>(route)

    const redirectUrl = response.data.data?.redirectUrl

    if (!redirectUrl) {
        throw new Error('Missing redirect url')
    }

    window.location.href = redirectUrl
}
